import { getRequestLocale, refreshTokenHandler } from '@brightdrop/auth-client';
import {
  type AxiosError,
  AxiosHeaders,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { CS_LOCALE_HEADER_NAME, DEFAULT_API_HEADERS } from './api.constants';
import { instance } from './apiBase';

/**
 * The reader's locale, read at request time and written after the caller's own headers, so this
 * stays the one answer to which language the platform should generate in. `getRequestLocale` is
 * used rather than the `Headers` helper the RTK clients call because Axios is handed a plain
 * object here.
 */
const localeHeader = (): Record<string, string> => ({
  [CS_LOCALE_HEADER_NAME]: getRequestLocale(),
});

/**
 * Interceptor which adds default and auth headers to BD API requests, fetching token as needed
 */

export const bdAuthRequestInterceptor = async (
  requestConfig: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
  if (requestConfig.bdExtra?.skipAuth) {
    return {
      ...requestConfig,
      headers: new AxiosHeaders({
        ...DEFAULT_API_HEADERS,
        ...requestConfig.headers,
        ...localeHeader(),
      }),
    };
  }

  const response = await refreshTokenHandler();
  const { accessToken: token } = response ?? {};

  if (token) {
    // if is BD request and authToken exists, apply to auth header
    return {
      ...requestConfig,
      headers: new AxiosHeaders({
        ...DEFAULT_API_HEADERS,
        Authorization: `Bearer ${token}`,
        ...requestConfig.headers,
        ...localeHeader(),
      }),
    };
  }

  // Non-BD requests go out untouched: no locale header, and none of the defaults above either.
  // This instance also reaches api.mapbox.com, where a custom header on a cross-origin request
  // costs a preflight that the geocoding endpoint neither needs nor answers.
  return requestConfig;
};

/**
 * Interceptor which detects errors related to auth token expiration, attempting to
 * refresh token and re-attempt request if possible
 */
export const bdAuthErrorResponseInterceptor = async (
  err: AxiosError<unknown>
): Promise<AxiosResponse<unknown> | AxiosError<unknown> | undefined> => {
  const originalRequest = err.config;

  if (
    err.isAxiosError &&
    !err.config?._retry &&
    (err.message === 'Network Error' || err.response?.status === 401)
  ) {
    if (originalRequest) {
      originalRequest._retry = true;
    }

    try {
      await refreshTokenHandler();
      return instance.request(err.config || {});
    } catch (authErr) {
      // TODO: [rebrand-internal] dev-facing log prefix "BrightDrop Web" — defer until a coordinated internal identifier cleanup (may affect Datadog/log filters)
      console.error('BrightDrop Web - auth token refresh failure', authErr);
      return Promise.reject(authErr);
    }
  }

  return Promise.reject(err);
};
